import { useState, useCallback, useRef, useEffect } from 'react';
import { Icon } from '@/components/Icon';
import { isios } from '@/hooks/useIsMobile';

// items is [{name: "valuename", element, height}]
export const GenericDropdown = ({ items = [], menuPaddingHeight, default_element="Select...", value, onChange, className="", disabled, ...args }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({});
  const dropdownRef = useRef(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const [returnFocusToElement, setReturnFocusToElement] = useState(null);

  const dropdownItems = items.filter(a => a.name);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        closeDropdown();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusIndex((prevIndex) => 
          (prevIndex + 1) % dropdownItems.length
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusIndex((prevIndex) => 
          (prevIndex - 1 + dropdownItems.length) % dropdownItems.length
        );
        break;
      case 'Tab':
        e.preventDefault(); // Prevent tabbing out of the dropdown
        break;
    }
  }, [isOpen, dropdownItems.length]);

  useEffect(() => {
    if (isOpen) {
      setReturnFocusToElement(dropdownRef.current.querySelector("button"));
    } else {
      returnFocusToElement?.focus();
      setFocusIndex(items.findIndex(a => a.name === value));
      setReturnFocusToElement(null);
    }
  }, [isOpen, value]);


  useEffect(() => {
    if (isOpen) {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.querySelector("button").contains(event.target) && !dropdownRef.current.querySelector("ul")?.contains(event.target)) {
          closeDropdown();
        }
      };

      const handleScrollOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.querySelector("ul")?.contains(event.target)) {
          event.preventDefault();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('wheel', handleScrollOutside, { passive: false });
      document.addEventListener('touchmove', handleScrollOutside, { passive: false });

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('wheel', handleScrollOutside);
        document.removeEventListener('touchmove', handleScrollOutside);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const dropdownItemElements = dropdownRef.current.querySelectorAll('[role="option"]');
      dropdownItemElements[focusIndex]?.focus();
    }
  }, [focusIndex, isOpen]);

  const openDropdown = () => {
    if (items.length == 0) return;

    setIsOpen(true);

    const modalDialogRect = dropdownRef.current.closest(".modal_dialog")?.getBoundingClientRect();

    const buttonRect = dropdownRef.current.querySelector("button").getBoundingClientRect();
    const dropdownHeight = items.reduce((acc, item) => acc + item.height, 0) + menuPaddingHeight;

    const spaceBelow = window.innerHeight - buttonRect.bottom - (isios ? 58 : 0);
    const spaceAbove = buttonRect.top - (modalDialogRect?.top || 0);

    let openAbove = false;

    // If there's not enough space below, but there is enough space above, open above
    if ((dropdownHeight+7) > spaceBelow && (dropdownHeight+7) < spaceAbove) {
      openAbove = true;
    }

    // If there's not enough space below, and the space above is at least 1.5x the space below, open above
    if ((dropdownHeight+7) > spaceBelow && (spaceBelow*1.5) < spaceAbove) {
      openAbove = true;
    }

    setDropdownPosition({openAbove: openAbove, spaceBelow: spaceBelow, spaceAbove: spaceAbove, buttonHeight: buttonRect.height, dropdownHeight: dropdownHeight});
  }

  const closeDropdown = () => {
    setIsOpen(false);
  }

  const dropdownMenuStyles = {};

  if (dropdownPosition.openAbove) {
    // dropdownMenuStyles.transform = `translateY(calc(-100% - ${dropdownPosition.buttonHeight}px))`;
    dropdownMenuStyles.top = Math.max(dropdownPosition.spaceAbove - dropdownPosition.dropdownHeight, 7)+"px";
    dropdownMenuStyles.maxHeight = (dropdownPosition.spaceAbove - menuPaddingHeight - 7)+"px";
  } else {
    dropdownMenuStyles.top = (dropdownPosition.spaceAbove + dropdownPosition.buttonHeight)+"px";
    dropdownMenuStyles.maxHeight = (dropdownPosition.spaceBelow - menuPaddingHeight - 7)+"px";
  }

  return (
    <div 
      className={`generic_dropdown ${className}`}
      ref={dropdownRef}
      onKeyDown={handleKeyDown}
      {...args}
    >
      <button
        type="button"
        onClick={() => !disabled ? (isOpen ? closeDropdown() : openDropdown()) : null}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        role="combobox"
        aria-controls="dropdown-list"
        className="dropdown_button"
        disabled={disabled}
      >
        <div>
          {items.find(a => a.name === value)?.element || default_element}
        </div>
        <div>
          <Icon name="menu-down-enlarged"/>
        </div>
      </button>
      {isOpen && (
        <ul
          role="listbox"
          className="dropdown_menu"
          aria-orientation="vertical"
          style={dropdownMenuStyles}
        >
          {items.map((item, index) => (
            item.name ? (
              <li
                key={item.name}
                role="option"
                aria-selected={item.name === value}
                tabIndex={0}
                onClick={() => {
                  closeDropdown();
                  onChange(item.name);
                }}
                className={`dropdown_item ${focusIndex === index ? "focused" : ""} ${item.name === value ? "selected" : ""}`}
              >
                {item.element}
              </li>
            ) : (
              <li key={index}>
                {item.element}
              </li>
            )
          ))}
        </ul>
      )}
    </div>
  );
};